import React, { useState } from 'react';
import { Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { CaretDown } from '@phosphor-icons/react';
import classNames from 'classnames';
import parse from 'html-react-parser';

import {
  HelpArticleContent,
  HelpArticleSection,
  HelpSectionBlock,
  HelpListItem,
  HelpImage,
} from './types';

interface Props {
  content: HelpArticleContent;
}

const ArticleContent: React.FC<Props> = (props) => {
  const { content } = props;

  // all sections are closed by default
  const [openedSections, setOpenedSections] = useState<string[]>([]);

  const handleSectionClick = (id: string) => {
    setOpenedSections((prevOpenedSections: string[]) => {
      if (prevOpenedSections.includes(id))
        return prevOpenedSections.filter((i: string) => i !== id);
      else return [...prevOpenedSections, id];
    });
  };

  const renderImage = (image: HelpImage) => {
    return (
      <div
        className={classNames(
          'max-w-full sm:max-w-[350px] mt-2 bg-white rounded-md',
          { 'p-4': !image.noPadding },
        )}
      >
        <img
          src={`https://crowdclip-media-assets.s3.ap-southeast-2.amazonaws.com/help/${image.name}.png`}
          alt={image.alt ?? 'Help Graphic'}
          className={classNames({
            'rounded-md': image.noPadding,
          })}
        />
      </div>
    );
  };

  const renderSectionBlock = (block: HelpSectionBlock) => {
    switch (block.type) {
      case 'text':
        return <div>{parse(block.text)}</div>;

      case 'subheading':
        return <h6 className="mt-2 font-semibold">{block.text}</h6>;

      case 'note':
        return <div className="text-sm italic">{parse(block.text)}</div>;

      case 'list':
        return (
          <ol className="w-full flex flex-col self-center m-0 pl-4 sm:pl-6 gap-4 list-decimal">
            {block.items.map((item: HelpListItem, index: number) => (
              <li key={index}>
                <div>{parse(item.text)}</div>

                {item.note && (
                  <div className="text-sm italic mt-1">{parse(item.note)}</div>
                )}

                {item.image && renderImage(item.image)}
              </li>
            ))}
          </ol>
        );

      case 'element':
        return block.content;

      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col p-3 sm:p-6 bg-grey-100 rounded-md">
      {content?.sections.map((section: HelpArticleSection, index: number) => (
        <React.Fragment key={section.id}>
          <Accordion
            expanded={openedSections.includes(section.id)}
            onChange={() => handleSectionClick(section.id)}
            className="shadow-none bg-transparent"
            disableGutters
            sx={{
              '.MuiAccordionSummary-root': { minHeight: 0, gap: '0.5rem' },
              ':before': { display: 'none' },
            }}
            id={section.id}
          >
            <AccordionSummary
              expandIcon={
                <CaretDown
                  weight="bold"
                  size="1.1rem"
                  className="leading-none group-hover:text-primary-900"
                />
              }
              className="group p-0"
              sx={{ '.MuiAccordionSummary-content': { margin: 0 } }}
            >
              <div>
                <h5 className="group-hover:text-primary-900">
                  {parse(section.heading)}
                </h5>

                {section.intro && (
                  <div className="mt-1 italic">{section.intro}</div>
                )}
              </div>
            </AccordionSummary>

            <AccordionDetails className="p-0 pt-2 flex flex-col gap-2">
              {section.note && <div>{parse(section.note)}</div>}

              {section.blocks.map((block: HelpSectionBlock, index: number) => (
                <React.Fragment key={index}>
                  {renderSectionBlock(block)}
                </React.Fragment>
              ))}
            </AccordionDetails>
          </Accordion>

          {index !== content.sections.length - 1 && <hr className="my-5" />}
        </React.Fragment>
      ))}
    </div>
  );
};

export default ArticleContent;
